import {z} from '@hono/zod-openapi';

/*
 * The `createdGte` shape gate. Together the patterns below accept the ISO-8601 spellings the
 * value is measured to take: a bare date, or a date and time separated by `T` or a space, with
 * optional seconds, optional fractional seconds and an optional `Z` or `±HH:MM` offset.
 *
 * The value lands in a Postgres `::timestamptz` cast, so anything the cast rejects used to fail in
 * the repository and travel as a `REPOSITORY_ERROR` **500** — an unhandled failure reachable from
 * an unvalidated query parameter. The gate turns that into the `400` the route already declares.
 * It is deliberately a `refine` rather than `.regex()`: a pattern would be published into the
 * generated OpenAPI document, and these operations are `hide: true`.
 */

/** `YYYY-MM-DD`, with the month and the day captured for the range check. */
const DATE_PATTERN = /^\d{4}-(\d{2})-(\d{2})$/;

/** The separator between the date and the time, if the value carries one at all. */
const DATE_TIME_SEPARATOR = /[T ]/;

/**
 * The three time spellings, each its own flat pattern: one combined regex nests its quantifiers,
 * which is both harder to read and what the unsafe-regex rule objects to.
 */
const TIME_PATTERNS = [/^\d{2}:\d{2}$/, /^\d{2}:\d{2}:\d{2}$/, /^\d{2}:\d{2}:\d{2}\.\d{1,6}$/];

/** `Z` or `±HH:MM`, taken off the end before the clock is read. */
const OFFSET_PATTERN = /(?:Z|[+-]\d{2}:\d{2})$/;

const FIRST_CALENDAR_UNIT = 1;
const LAST_MONTH = 12;
const LAST_DAY = 31;
const FIRST_CLOCK_UNIT = 0;
const LAST_HOUR = 23;
const LAST_MINUTE_OR_SECOND = 59;

const isWithin = (value: string, first: number, last: number) => {
  const parsed = Number(value);
  return parsed >= first && parsed <= last;
};

/**
 * `Date` is deliberately not used: `new Date(...)` is restricted in this repository, its ISO
 * parser rolls `2026-02-30` over to 2 March rather than rejecting it, and its fallback parser
 * accepts strings Postgres rejects (`'hello 2026'`) — which would leave the 500 in place for
 * exactly the inputs this gate exists for. The bounds are deliberately coarse: a day of 31 in a
 * 30-day month still reaches the cast and still answers 500, which the register records.
 */
const isLegacyTimestampLiteral = (value: string) => {
  const separator = value.search(DATE_TIME_SEPARATOR);
  const datePart = separator === -1 ? value : value.slice(0, separator);
  const dateFields = DATE_PATTERN.exec(datePart);

  if (dateFields === null) {
    return false;
  }

  const [, month = '', day = ''] = dateFields;

  if (
    !isWithin(month, FIRST_CALENDAR_UNIT, LAST_MONTH) ||
    !isWithin(day, FIRST_CALENDAR_UNIT, LAST_DAY)
  ) {
    return false;
  }

  if (separator === -1) {
    return true;
  }

  const timePart = value.slice(separator + 1).replace(OFFSET_PATTERN, '');
  const [hour = '', minute = '', second = '0'] = timePart.split(':');

  return (
    TIME_PATTERNS.some((pattern) => pattern.test(timePart)) &&
    isWithin(hour, FIRST_CLOCK_UNIT, LAST_HOUR) &&
    isWithin(minute, FIRST_CLOCK_UNIT, LAST_MINUTE_OR_SECOND) &&
    isWithin(second, FIRST_CLOCK_UNIT, LAST_MINUTE_OR_SECOND)
  );
};

const NUL = '\u0000';

/**
 * The same gate the public list query carries: `customerId` reaches the raw query's parameters,
 * and a NUL in a `text` comparison is a rejected statement — a `REPOSITORY_ERROR` 500 and a Sentry
 * event where the operation declares a 400. `createdGte` is already closed to it by the shape
 * check below.
 */
const carriesNoNulByte = (value: string) => !value.includes(NUL);

/**
 * The internal list query: the public filter plus a `createdGte` lower bound on the report's
 * creation timestamp.
 */
export const priceReportInternalListQuerySchema = z.object({
  customerId: z
    .string()
    .refine(carriesNoNulByte, 'customerId must not carry a NUL character')
    .optional(),
  createdGte: z
    .string()
    .refine(isLegacyTimestampLiteral, 'createdGte must be an ISO-8601 date or date-time')
    .optional(),
});
